import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Typography
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import ButtonBase from 'design-system/button'
import { useEffect, useMemo, useState } from 'react'
import { IEstablishmentResponse, IModulePermissionType } from 'types'
import { toggleBatchHealthcarePermissions } from 'services/establishment'
import { useToast } from 'services/context/toast'
import { useAppSelector, useAppDispatch } from 'reduxStore/store'
import { setCurrentEstablishment } from 'reduxStore/slices/Establishment'

export const AVAILABLE_MODULES: {
  id: IModulePermissionType
  name: string
  color: 'primary' | 'secondary' | 'success'
}[] = [
  { id: 'ecg_analysis', name: 'Análise de ECG', color: 'primary' as const },
  {
    id: 'cxray_analysis',
    name: 'Análise de Raios X',
    color: 'secondary' as const
  },
  {
    id: 'patient_monitoring',
    name: 'Monitoramento de Pacientes',
    color: 'success' as const
  },
  {
    id: 'chat',
    name: 'Chat',
    color: 'primary' as const
  }
]

interface PermissionModalProps {
  permissionModalOpen: boolean
  selectedEstablishment: IEstablishmentResponse | null
  onConfirm?: (modules: IModulePermissionType[]) => void
  onClose?: () => void
  disableApiCalls?: boolean
}

const PermissionModal: React.FC<PermissionModalProps> = ({
  permissionModalOpen,
  selectedEstablishment,
  onConfirm,
  onClose,
  disableApiCalls = false
}) => {
  const [selectedModules, setSelectedModules] = useState<
    IModulePermissionType[]
  >([])
  const toast = useToast()
  const dispatch = useAppDispatch()
  const currentEstablishment = useAppSelector(state => state.establishment.data)

  const previousModules = useMemo(() => {
    return selectedEstablishment?.modules_permission || []
  }, [selectedEstablishment])

  const hasPermissionChanges = useMemo(() => {
    if (selectedModules.length !== previousModules.length) {
      return true
    }

    const previousSet = new Set(previousModules)

    return !selectedModules.every(module => previousSet.has(module))
  }, [selectedModules, previousModules])

  useEffect(() => {
    if (permissionModalOpen) {
      setSelectedModules(previousModules)
    }
  }, [permissionModalOpen, previousModules])

  const handleSavePermissions = async () => {
    // If API calls are disabled, just call onConfirm directly
    if (disableApiCalls) {
      if (onConfirm) {
        onConfirm(selectedModules)
      }
      return
    }

    // Guard: prevent API call if no valid establishment ID
    if (!selectedEstablishment?.establishment_id) {
      toast.handler({
        content: 'Erro: Estabelecimento não encontrado',
        severity: 'error'
      })
      return
    }

    // Original API call logic
    const previousSet = new Set(previousModules)
    const selectedSet = new Set(selectedModules)

    const addedModules = selectedModules.filter(
      module => !previousSet.has(module)
    )
    const removedModules = previousModules.filter(
      module => !selectedSet.has(module)
    )

    const changedModules = [...addedModules, ...removedModules]

    const response = await toggleBatchHealthcarePermissions(
      changedModules.map(moduleId => ({
        establishment_id: selectedEstablishment.establishment_id,
        module_name: moduleId
      }))
    )

    if (response.success) {
      toast.handler({
        content: 'Permissões salvas com sucesso',
        severity: 'success'
      })

      // Check if the current establishment is the one being edited, if yes, update the Redux store and storage with the new permissions
      if (
        currentEstablishment?.establishment_id &&
        selectedEstablishment?.establishment_id ===
          currentEstablishment?.establishment_id
      ) {
        // Practically, we know that the backend will return the updated permissions, but to avoid any desync we will update the permissions in the frontend based on the changes made
        const updatedEstablishment = {
          ...currentEstablishment,
          modules_permission: selectedModules
        }
        dispatch(setCurrentEstablishment(updatedEstablishment))
      }

      if (onConfirm) {
        onConfirm(selectedModules)
      }
    } else {
      toast.handler({ content: 'Erro ao salvar permissões', severity: 'error' })
    }
  }

  const handleModuleToggle = (moduleId: IModulePermissionType) => {
    setSelectedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    )
  }
  return (
    <Dialog
      open={permissionModalOpen}
      onClose={onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>
            Gerenciar Permissões - {selectedEstablishment?.establishment_name}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          Selecione os módulos que este estabelecimento terá acesso:
        </Typography>
        <Grid container spacing={2}>
          {AVAILABLE_MODULES.map(module => (
            <Grid size={{ xs: 12, md: 6 }} key={module.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedModules.includes(module.id)}
                    onChange={() => handleModuleToggle(module.id)}
                    color={module.color}
                  />
                }
                label={
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
                      {module.name}
                    </Typography>
                    <Chip
                      label={module.name}
                      size='small'
                      color={module.color}
                      variant='outlined'
                    />
                  </Box>
                }
              />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'defaultComponent.main',
            borderRadius: 1
          }}
        >
          <Typography variant='body2' fontWeight='bold' gutterBottom>
            Módulos Selecionados: {selectedModules.length}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {selectedModules.map(moduleId => {
              const module = AVAILABLE_MODULES.find(m => m.id === moduleId)
              return module ? (
                <Chip
                  key={moduleId}
                  label={module.name}
                  size='small'
                  color={module.color}
                  onDelete={() => handleModuleToggle(moduleId)}
                />
              ) : null
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <ButtonBase onClick={onClose} color='inherit'>
          Cancelar
        </ButtonBase>
        <ButtonBase
          onClick={handleSavePermissions}
          variant='contained'
          color='primary'
          disabled={
            !hasPermissionChanges ||
            (!disableApiCalls && !selectedEstablishment?.establishment_id)
          }
        >
          {disableApiCalls ? 'Selecionar' : 'Salvar'}
        </ButtonBase>
      </DialogActions>
    </Dialog>
  )
}

export default PermissionModal
